"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useWaveWebSocket } from "./useWaveWebSocket";
import { WsDropUpdateMessage, WsMessageType, WsTypingMessage } from "@/helpers/Types";
import { ApiProfileMin } from "@/generated/models/ApiProfileMin";
/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface TypingEntry {
  profile: ApiProfileMin;
  lastTypingAt: number; // our local receive time (ms)
}

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

const TYPING_WINDOW_MS = 5_000; // still typing if ≤ 5 s old
const CLEANUP_INTERVAL_MS = 1_000; // prune/check once per second

/* ------------------------------------------------------------------ */
/*  Helper to convert active typers → human string                    */
/* ------------------------------------------------------------------ */

function buildTypingString(entries: TypingEntry[]): string {
  if (entries.length === 0) return "";

  // Highest‑level first (undefined → 0)
  const sorted = entries.sort((a, b) => (b.profile.level ?? 0) - (a.profile.level ?? 0));
  const names = sorted.map(e => e.profile.handle);
  if (names.length === 1) {
    return `${names[0]} is typing`;
  }
  if (names.length === 2) {
    return `${names[0]}, ${names[1]} are typing`;
  }
  return `${names[0]}, ${names[1]} and ${names.length - 2} more people are typing`;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

/**
 * React hook that returns a live “is‑typing” label for a wave.
 *
 * @param waveId    Wave/channel ID being viewed.
 * @param myHandle  Handle of current user (events from this handle are ignored).
 */
export function useWaveIsTyping(waveId, myHandle) {
  const $ = _c(11);
  const {
    socket
  } = useWaveWebSocket(waveId);
  const [typingMessage, setTypingMessage] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const typersRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      typersRef.current.clear();
      setTypingMessage("");
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== waveId) {
    t2 = [waveId];
    $[2] = waveId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== myHandle || $[5] !== socket || $[6] !== waveId) {
    t3 = () => {
      if (!socket) {
        return;
      }
      const onMessage = event => {
        let msg;
        ;
        try {
          msg = JSON.parse(event.data);
        } catch (t5) {
          const err = t5;
          console.error("Bad WebSocket JSON", err);
          return;
        }
        if (msg.type === WsMessageType.DROP_UPDATE) {
          typersRef.current.delete(msg.data?.author.handle ?? "");
        }
        if (msg.type !== WsMessageType.USER_IS_TYPING) {
          return;
        }
        const data = msg.data;
        if (!data || data.wave_id !== waveId) {
          return;
        }
        if (data.profile?.handle === myHandle) {
          return;
        }
        if (!data.profile?.handle) {
          return;
        }
        typersRef.current.set(data.profile.handle, {
          profile: data.profile,
          lastTypingAt: Date.now()
        });
      };
      socket.addEventListener("message", onMessage);
      return () => socket.removeEventListener("message", onMessage);
    };
    t4 = [socket, waveId, myHandle];
    $[4] = myHandle;
    $[5] = socket;
    $[6] = waveId;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const intervalId = setInterval(() => {
        const now = Date.now();
        typersRef.current.forEach((entry, handle) => {
          if (now - entry.lastTypingAt > TYPING_WINDOW_MS) {
            typersRef.current.delete(handle);
          }
        });
        const newMessage = buildTypingString(Array.from(typersRef.current.values()));
        setTypingMessage(prev => prev === newMessage ? prev : newMessage);
      }, CLEANUP_INTERVAL_MS);
      return () => clearInterval(intervalId);
    };
    t6 = [];
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  return typingMessage;
}